package one.devsbox.www.ui.users.detail

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.EditOutline
import compose.assets.svg.ResetOutline
import compose.assets.svg.TrashOutline
import kotlinx.coroutines.launch
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.ads.UserAction
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.tools.PermissionWrapperEditor
import one.devsbox.www.www_portal.generated.resources.Res
import one.devsbox.www.www_portal.generated.resources.ui_users_detail_reset_success_msg
import one.devsbox.www.www_portal.generated.resources.ui_users_detail_btn_edit
import one.devsbox.www.www_portal.generated.resources.ui_users_detail_btn_reset_pass
import one.devsbox.www.www_portal.generated.resources.ui_users_detail_btn_delete
import org.jetbrains.compose.resources.StringResource
import tk.mallumo.compose.navigation.navTo_UserEUI
import tk.mallumo.compose.navigation.navTo_UsersActivityUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

@VM
class UserDVM : NavigationViewModel() {
    private lateinit var ctx: UserDCtx

    data class Data(
        val user: SharedIO.User, 
        val permissions: PermissionWrapperEditor,
        val logs: List<SharedIO.UserActivity>
    )

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    var showDeleteConfirmation by mutableStateOf(false)
        private set

    var state by mutableStateOf<DataState<Data>>(DataState.Loading())
        private set

    var infoDialogMessage by mutableStateOf<StringResource?>(null)
        private set

    interface Action {
        fun reload()
        fun edit()

        fun resetPassword()

        fun delete()
        fun confirmDelete()
        fun dismissDeleteConfirmation()
        fun back()
        fun dismissInfoDialog()
        fun viewAuditTrail()
    }

    val action = object : Action {
        override fun reload() {
            state = DataState.Loading(state.entry)
            loadUser(ctx.args.id)
        }

        override fun back() {
            ctx.nav.up()
        }

        override fun edit() {
            ctx.nav.navTo_UserEUI(ArgsId(ctx.args.id))
        }

        override fun dismissInfoDialog() {
            infoDialogMessage = null
        }

        override fun resetPassword() {
            val currentData = state.entry ?: return
            state = DataState.Loading(currentData)
            scope.launch {
                runCatching {
                    Repository.ads.user.resetPassword(ctx.args.id)
                }.onSuccess {
                    state = DataState.Result(currentData)
                    infoDialogMessage = Res.string.ui_users_detail_reset_success_msg
                    setupActions(currentData)
                }.onFailure {
                    state = DataState.Error(it.message ?: "Failed to reset password")
                    setupActions(currentData)
                }
            }
        }

        override fun delete() {
            showDeleteConfirmation = true
        }

        override fun dismissDeleteConfirmation() {
            showDeleteConfirmation = false
        }

        override fun confirmDelete() {
            showDeleteConfirmation = false
            val currentData = state.entry ?: return
            state = DataState.Loading(currentData)
            scope.launch {
                runCatching {
                    Repository.ads.user.delete(ctx.args.id)
                }.onSuccess {
                    ctx.nav.up()
                }.onFailure {
                    state = DataState.Error(it.message ?: "Failed to delete user")
                    setupActions(currentData)
                }
            }
        }

        override fun viewAuditTrail() {
            ctx.nav.navTo_UsersActivityUI(ArgsId(ctx.args.id))
        }
    }

    fun setup(ctx: UserDCtx) {
        this.ctx = ctx
        if (state is DataState.Loading) {
            state = DataState.Loading()
        }
        loadUser(ctx.args.id)
    }

    private fun loadUser(id: Long) {
        scope.launch {
            runCatching {
                val user = Repository.ads.user.get(id)
                val mask = UserAction.ADD.mask + UserAction.CHANGE.mask + UserAction.DELETE.mask
                val activities = Repository.ads.user.activity(id, 0, mask).items
                Data(user, PermissionWrapperEditor(user.permission), activities)
            }.onSuccess { data ->
                state = DataState.Result(data)
                setupActions(data)
            }.onFailure {
                state = DataState.Error(it.message ?: "Unknown error")
                contentActions = emptyList()
            }
        }
    }

    private fun setupActions(data: Data) {
        contentActions = buildList {
            if (ctx.me.permission.user.write) {
                add(
                    ScreenContentAction(
                        text = Res.string.ui_users_detail_btn_edit,
                        color = ctx.colorPrimaryContainer,
                        icon = Svg.EditOutline
                    ) {
                        action.edit()
                    }
                )
                add(
                    ScreenContentAction(
                        text = Res.string.ui_users_detail_btn_reset_pass,
                        color = ctx.colorSecondaryContainer,
                        icon = Svg.ResetOutline
                    ) {
                        action.resetPassword()
                    }
                )
                if (ctx.me.permission.user.delete) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_users_detail_btn_delete,
                            color = ctx.colorErrorContainer,
                            icon = Svg.TrashOutline
                        ) {
                            action.delete()
                        }
                    )
                }
            }
        }
    }

    override fun onRelease() {}
}

